/* eslint-disable filetree-structure/services -- a part of the `catalog`
service, imported by path rather than re-exported. The rule allows only
platform extensions beside the folder-named file. */
import type { ApiServices } from "$src/core/contexts/api";
import type { PageDto, WatchlistDto } from "$src/core/models/dto";
import Logging from "$src/core/services/logging/logging";
import Storage from "$src/core/services/storage/storage";

export type CarCatalogData = {
  storefront: PageDto | undefined;
  watchlist: WatchlistDto | undefined;
};

// A cold in-car launch can start before the phone's radio is up.
const RETRY_DELAYS_MS = [2_000, 5_000, 10_000];

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const fetchOnce = async (api: ApiServices): Promise<CarCatalogData> => {
  const profileId = Storage.get("auth.profileId");
  const [storefront, watchlist] = await Promise.all([
    api.getStorefront().catch((error: unknown) => {
      Logging.warn("Car catalog: storefront fetch failed", { error });
      return undefined;
    }),
    profileId
      ? api.getWatchlist(profileId).catch((error: unknown) => {
          Logging.warn("Car catalog: watchlist fetch failed", { error });
          return undefined;
        })
      : Promise.resolve(undefined),
  ]);
  return { storefront, watchlist };
};

/** Retries only while BOTH fetches are empty: a missing watchlist still yields a catalog. */
const fetchCarCatalogData = async (
  api: ApiServices,
  shouldRetry: () => boolean,
): Promise<CarCatalogData> => {
  // Sequential by design: each attempt waits on the previous failure.
  /* eslint-disable no-await-in-loop */
  for (let attempt = 0; ; attempt += 1) {
    const data = await fetchOnce(api);
    if (data.storefront || data.watchlist) return data;

    const delay = RETRY_DELAYS_MS.at(attempt);
    if (delay === undefined || !shouldRetry()) return data;

    Logging.warn("Car catalog empty; retrying", { attempt, delay });
    await sleep(delay);
    if (!shouldRetry()) return data;
  }
  /* eslint-enable no-await-in-loop */
};

export default fetchCarCatalogData;
